import { ApiError } from '@/api/middlewares/error-toast-middleware';

export type FieldErrors = Record<string, string[]>;

export type NormalizedError =
  | {
      kind: 'validation';
      status: number;
      message: string;
      fieldErrors: FieldErrors;
      code?: string;
      details?: unknown;
    }
  | {
      kind: 'problem';
      status: number;
      message: string;
      code?: string;
      details?: unknown;
    }
  | { kind: 'network'; status: 0; message: string }
  | {
      kind: 'server';
      status: number;
      message: string;
      code?: string;
      details?: unknown;
    }
  | { kind: 'unknown'; status: number; message: string };

// biome-ignore lint/suspicious/noExplicitAny: <any> is needed here
export function isValidationProblemDetails(x: any): x is {
  type?: string;
  title?: string;
  status?: number;
  errors: Record<string, string[]>;
} {
  return (
    !!x && typeof x === 'object' && !!x.errors && typeof x.errors === 'object'
  );
}

export function normalizeApiError(err: unknown): NormalizedError {
  if (!(err instanceof ApiError)) {
    return {
      kind: 'unknown',
      status: -1,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      message: (err as any)?.message ?? 'Unknown error',
    };
  }

  // Network
  if (err.status === 0) {
    return {
      kind: 'network',
      status: 0,
      message: 'Network error. Check your connection and try again.',
    };
  }

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const d: any = err.details;
  const msg = (d?.detail ??
    d?.title ??
    err.message ??
    `HTTP ${err.status}`) as string;

  // Validation bag
  if (
    (err.status === 400 || err.status === 422) &&
    isValidationProblemDetails(d)
  ) {
    return {
      kind: 'validation',
      status: err.status,
      message: msg || 'Please check your input.',
      fieldErrors: d.errors,
      details: d,
    };
  }

  // Other 4xx → problem
  if (err.status >= 400 && err.status < 500) {
    return {
      kind: 'problem',
      status: err.status,
      message: msg || 'Request error.',
      details: d,
    };
  }

  // 5xx → server
  if (err.status >= 500) {
    return {
      kind: 'server',
      status: err.status,
      message:
        msg ||
        'Something went wrong. Please try again later or contact support',
      details: d,
    };
  }

  // Fallback
  return {
    kind: 'unknown',
    status: err.status ?? -1,
    message: msg || 'Unexpected error',
  };
}
